import { callsBefore } from './callingWait'
import { mergePendingRequests } from './pendingRequests'
import type { ApprovalView, CounterView, Delta, ParkScope, PendingRequestView, QueueAlert, Snapshot, Ticket } from './types'

// The screen's copy of a branch.
//
// Every screen holds one of these and keeps it right from two messages: a snapshot
// when it connects, and a delta for each committed change. The logic is here, as
// pure functions over plain data, because this is the part where being subtly wrong
// shows a customer a queue position that is not true — and pure functions are the
// part that can be tested without a browser or a server.

export interface BranchState {
  branchId: string
  branchName: string
  businessDate: string
  version: number
  tickets: Ticket[]
  counters: CounterView[]
  queues: Snapshot['queues']
  services: Snapshot['services']
  /** When the last message arrived, for the "last updated" line on a wall display. */
  updatedAt: string
  /** The oldest business date still holding open tickets, when it is not today's:
   *  a day nobody ended. Empty in the ordinary case. */
  unclosedFrom: string
  /** The published version of the branch's setup this state was read under. Zero
   *  from a server too old to send one, which is why the comparison below only
   *  resyncs on a delta that names a version. */
  configVersion: number
  /** The queues over their alert level: from the snapshot, then replaced whole by
   *  each "alerts" message. A delta does not carry them and does not touch them. */
  alerts: QueueAlert[]
  /** What the Counter page offers a request: which kinds wait for a decision.
   *  From the snapshot only — a delta carries no branch
   *  settings, and the settings themselves are saved straight to the branch
   *  rather than published, so a resync on a publish is not what would catch a
   *  change made mid-session anyway. Absent from a server too old to send it. */
  approval?: ApprovalView
  /** The branch's undecided desk requests, oldest first: from the snapshot, then
   *  kept current by lib/pendingRequests.mergePendingRequests on every delta — a
   *  delta carries no list of its own, only each changed desk's own request. */
  requests: PendingRequestView[]
  /** The booking policy's booked-first rule, from the snapshot; absent reads as on, its default. */
  bookedFirst?: boolean
  /** The day's calls, ended visits included: from the snapshot, then every called
   *  ticket that leaves the open set (recentCalls lays the open tickets over it). */
  calls?: BoardCall[]
  /** The business day those calls belong to; a ticket of a later day empties them. */
  callsDay?: string
}

/** One ticket's latest call, as a board lists it. */
export interface BoardCall {
  id: string
  displayNumber: string
  counterId: string
  queueId?: string
  calledAt: string
  businessDate: string
}

/** How many calls a state keeps (engine.CallsKept). */
export const CALLS_KEPT = 100

export function fromSnapshot(snapshot: Snapshot): BranchState {
  // Every list is defended, even though the server sends empty lists rather than
  // null. A screen in a branch runs for months and is the last thing that should
  // fail because a list arrived in an unexpected shape.
  return {
    branchId: snapshot.branchId,
    branchName: snapshot.branchName,
    businessDate: snapshot.businessDate,
    version: snapshot.version,
    tickets: [...(snapshot.tickets ?? [])].sort(byIssuedAt),
    counters: [...(snapshot.counters ?? [])].sort(bySortOrder),
    queues: snapshot.queues ?? [],
    services: snapshot.services ?? [],
    updatedAt: snapshot.at,
    unclosedFrom: unclosedFrom(snapshot.tickets ?? [], snapshot.businessDate),
    configVersion: snapshot.configVersion ?? 0,
    alerts: snapshot.alerts ?? [],
    approval: snapshot.approval,
    requests: snapshot.requests ?? [],
    bookedFirst: snapshot.bookedFirst ?? true,
    calls: (snapshot.calls ?? []).map((c) => ({
      id: c.ticketId,
      displayNumber: c.displayNumber,
      counterId: c.counterId,
      queueId: c.queueId,
      calledAt: c.calledAt,
      businessDate: snapshot.businessDate,
    })),
    callsDay: snapshot.businessDate,
  }
}

/** The alerts message: the branch's whole list, replacing the last one. */
export function withAlerts(state: BranchState, alerts: QueueAlert[]): BranchState {
  return { ...state, alerts }
}

/**
 * The oldest day still carrying open tickets, when that day is not today.
 *
 * The server sends this on the snapshot as well, and the duplication is deliberate
 * for the same reason queue depth is recomputed below: a delta carries tickets and
 * not this, so a value taken from the snapshot and left alone would still be
 * warning about Tuesday several seconds after somebody closed Tuesday. Derived
 * here, it disappears the moment the tickets do.
 *
 * Business dates are written as YYYY-MM-DD, which compares as a string in the same
 * order it falls in.
 */
function unclosedFrom(tickets: Ticket[], today: string): string {
  let oldest = ''
  for (const ticket of tickets) {
    const day = ticket.businessDate
    if (!day || !today || day >= today) continue
    if (!oldest || day < oldest) oldest = day
  }
  return oldest
}

/**
 * Whether a delta continues from the state we hold.
 *
 * A delta says which version it applies to. If that is not the version we have, a
 * message was missed — the screen slept, the network dropped one — and applying it
 * anyway would leave a queue that looks plausible and is wrong. The screen asks for
 * a fresh snapshot instead.
 *
 * A delta at or below our version is one we have already applied. That happens
 * legitimately: subscribing and snapshotting are not one atomic act, so a change
 * committed in between arrives twice. Skipping it is correct.
 */
export function deltaFits(state: BranchState, delta: Delta): 'apply' | 'skip' | 'resync' {
  if (delta.branchId !== state.branchId) return 'skip'
  if (delta.version <= state.version) return 'skip'
  if (delta.from !== state.version) return 'resync'
  // Somebody published. The tickets on this delta would apply cleanly, and applying
  // them would leave the screen holding today's work against yesterday's branch —
  // the old menu in the lobby, a retired queue on the board, a desk drawing from a
  // pool its profile no longer names. None of that is on a delta, so there is
  // nothing to patch it from and the whole snapshot is asked for again.
  //
  // Only when the delta names a version: a server too old to send one must not put
  // every screen in the building into a resync loop.
  if (delta.configVersion !== undefined && delta.configVersion !== state.configVersion) {
    return 'resync'
  }
  return 'apply'
}

export function applyDelta(state: BranchState, delta: Delta): BranchState {
  const tickets = new Map(state.tickets.map((t) => [t.id, t]))
  const before = new Map(tickets)

  for (const ticket of delta.tickets ?? []) {
    tickets.set(ticket.id, ticket)
  }
  for (const id of delta.removed ?? []) {
    tickets.delete(id)
  }

  // A called ticket leaving the open set stays on the boards, as it was last seen.
  const uncalled = new Set(delta.uncalled ?? [])
  let calls = delta.dayEnded ? [] : (state.calls ?? [])
  let callsDay = state.callsDay ?? ''
  for (const t of delta.tickets ?? []) {
    if (t.businessDate > callsDay) callsDay = t.businessDate
  }
  for (const id of delta.removed ?? []) {
    const was = before.get(id)
    const call = was && !uncalled.has(id) && !delta.dayEnded ? callOf(was, calls.find((c) => c.id === id)) : undefined
    calls = calls.filter((c) => c.id !== id)
    if (call) calls = [call, ...calls]
  }
  calls = calls
    .filter((c) => !uncalled.has(c.id) && c.businessDate >= callsDay)
    .sort(newestCall)
    .slice(0, CALLS_KEPT)

  const counters = state.counters.slice()
  for (const changed of delta.counters ?? []) {
    const at = counters.findIndex((c) => c.counterId === changed.counterId)
    if (at >= 0) counters[at] = changed
    else counters.push(changed)
  }

  const next: BranchState = {
    ...state,
    version: delta.version,
    tickets: [...tickets.values()].sort(byIssuedAt),
    counters: counters.sort(bySortOrder),
    updatedAt: delta.at,
    requests: mergePendingRequests(state.requests, delta.counters ?? []),
    calls,
    callsDay,
  }
  // Queue depth is recomputed from the tickets rather than sent: the two would
  // otherwise be able to disagree on the same screen, and the tickets are the ones
  // the operator can see for themselves.
  next.queues = recountQueues(next)
  next.unclosedFrom = unclosedFrom(next.tickets, next.businessDate)
  return next
}

function recountQueues(state: BranchState): BranchState['queues'] {
  const now = Date.parse(state.updatedAt) || Date.now()

  return state.queues.map((q) => {
    let waiting = 0
    let longest = 0
    let breaching = 0

    for (const ticket of state.tickets) {
      if (ticket.state !== 'waiting' || ticket.queueId !== q.queueId) continue
      waiting++
      const waited = Math.max(0, Math.round((now - Date.parse(ticket.issuedAt)) / 1000))
      if (waited > longest) longest = waited
      if (ticket.serviceLevelSec && waited > ticket.serviceLevelSec) breaching++
    }
    return { ...q, waiting, longestWaitSec: longest, breaching }
  })
}

/**
 * waitingIn returns the queue's line in the order the calling rules fall back to:
 * sent to the front first, then the longest calling wait — see lib/callingWait.
 *
 * It used to be issue order, which is the same thing only for as long as nobody
 * rejoins a line. A customer transferred, put back or recycled carries the waiting
 * they have done, and the server ranks them by it.
 */
export function waitingIn(state: BranchState, queueId: string, now: number = Date.now()): Ticket[] {
  return state.tickets
    .filter((t) => t.state === 'waiting' && t.queueId === queueId)
    .sort((a, b) => callsBefore(a, b, now, state.bookedFirst ?? true))
}

/**
 * positionOf is how many people in this ticket's own line are ahead of it, counting
 * from one. The server's position also counts the parked customers a desk will take
 * first; this is the line alone.
 */
export function positionOf(state: BranchState, ticket: Ticket, now: number = Date.now()): number {
  if (ticket.state !== 'waiting') return 0
  const line = waitingIn(state, ticket.queueId ?? '', now)
  const at = line.findIndex((t) => t.id === ticket.id)
  return at < 0 ? 0 : at + 1
}

/** ticketAt returns whatever the given desk is dealing with. */
export function ticketAt(state: BranchState, counterId: string): Ticket | undefined {
  return state.tickets.find(
    (t) =>
      t.calledToCounterId === counterId &&
      (t.state === 'called' || t.state === 'serving' || t.state === 'wrapup'),
  )
}

/** recentCalls are the numbers the hall display shows, most recent first. */
/**
 * The calls a board shows, newest first.
 *
 * desks narrows it to the windows this particular board is for. Empty means all of
 * them, which is the ordinary branch: one hall, one board, the windows in front of
 * you.
 *
 * The narrowing matters most where it is least visible. On a branch's upstairs
 * board, five downstairs desks calling do not merely add noise — they push the
 * number somebody upstairs is waiting for off the end of the list, and that person
 * misses their turn while looking straight at a working screen.
 */
export function recentCalls(state: BranchState, limit = 6, desks: string[] = []): BoardCall[] {
  const mine = desks.length === 0 ? () => true : (c: BoardCall) => Boolean(c.counterId && desks.includes(c.counterId))
  // The calls kept, with each open ticket laid over its own: a ticket back in a line is no call.
  const byId = new Map<string, BoardCall>()
  for (const c of state.calls ?? []) {
    if (c.businessDate >= (state.callsDay ?? '')) byId.set(c.id, c)
  }
  for (const t of state.tickets) {
    const call = callOf(t, byId.get(t.id))
    if (call) byId.set(t.id, call)
    else byId.delete(t.id)
  }
  return [...byId.values()].filter(mine).sort(newestCall).slice(0, limit)
}

/** States whose ticket a board lists once called: still at the desk, finished, or not come. */
const LISTED: ReadonlySet<Ticket['state']> = new Set(['called', 'serving', 'wrapup', 'no_show', 'closed'])

/** A ticket's latest call, or undefined when it has none a board shows. prior keeps the desk a finished ticket no longer names. */
function callOf(t: Ticket, prior?: BoardCall): BoardCall | undefined {
  if (!t.lastCalledAt || walkedIn(t) || !LISTED.has(t.state)) return undefined
  return {
    id: t.id,
    displayNumber: t.displayNumber,
    counterId: t.calledToCounterId || prior?.counterId || '',
    queueId: t.queueId ?? prior?.queueId,
    calledAt: t.lastCalledAt,
    businessDate: t.businessDate,
  }
}

const newestCall = (a: BoardCall, b: BoardCall) => Date.parse(b.calledAt) - Date.parse(a.calledAt)

/** The desk a listed call went to, for a board drawing its number. */
export function callDesk(state: BranchState | null | undefined, ticketId: string): string | undefined {
  if (!state) return undefined
  return state.tickets.find((t) => t.id === ticketId)?.calledToCounterId || state.calls?.find((c) => c.id === ticketId)?.counterId
}

/**
 * Whether this ticket was served straight from the desk, never having waited.
 *
 * A walk-in is issued and called in the same command, so its first call is the
 * very moment it was issued — which no ticket that waited in a line can share. It
 * is read that way rather than from a field of its own because it has to be read
 * on the hall board, which is sent the two times and not the source. A walk-in was
 * never announced, and putting its number in the board's recent calls would push
 * off the end the number somebody in the hall is actually waiting to see.
 */
export function walkedIn(ticket: Ticket): boolean {
  return Boolean(ticket.firstCalledAt) && ticket.firstCalledAt === ticket.issuedAt
}

/** Whether a clerk's delay is still holding this customer back. */
export function isHeld(ticket: Ticket, now: number = Date.now()): boolean {
  if (!ticket.held) return false
  return !ticket.backAt || Date.parse(ticket.backAt) > now
}

/** Whole minutes until a held customer comes back, never less than one. */
export function backInMinutes(ticket: Ticket, now: number = Date.now()): number {
  if (!ticket.backAt) return 1
  return Math.max(1, Math.ceil((Date.parse(ticket.backAt) - now) / 60_000))
}

/**
 * How long a customer has been standing aside.
 *
 * The server calls a pooled customer by this rather than by their issue time, and
 * so does every figure this screen shows about them. A parked ticket judged by the
 * hour it arrived would look like the most patient person in the branch.
 */
export function parkedSeconds(ticket: Ticket, now: number = Date.now()): number {
  if (!ticket.parkedAt) return 0
  return Math.max(0, Math.round((now - Date.parse(ticket.parkedAt)) / 1000))
}

/** parkedPool is the branch's parked customers, longest aside first. */
export function parkedPool(state: BranchState, now: number = Date.now()): Ticket[] {
  return state.tickets
    .filter((t) => t.state === 'parked')
    .sort((a, b) => parkedSeconds(b, now) - parkedSeconds(a, now))
}

/**
 * Whether Call next at this desk may take this parked customer. A desk's own hold
 * is not (owner, 29 Oct): only Call again brings it back.
 *
 * A mirror of the server's own rule, and not the authority: the server decides
 * again and refuses if this is wrong. What it decides here is only whether to offer
 * the button, because a button that is always refused teaches an operator that the
 * screen cannot be trusted.
 */
export function callableFromPool(
  ticket: Ticket,
  profile: { queueIds: readonly string[]; poolScopes?: readonly string[] },
  counterId: string,
  staffId = '',
  now: number = Date.now(),
): boolean {
  if (ticket.state !== 'parked' || !ticket.parkScope || !ticket.parkedAt) return false
  // Not yet: the clerk who parked them said to call them after a delay.
  if (isHeld(ticket, now)) return false
  // Sent here: Call next takes them first. This desk's own hold: Call again only.
  if (heldFor(ticket, counterId, staffId)) return sentTo(ticket, counterId, staffId)
  // Anybody else's: only out of the branch pool, by a profile drawing it and serving their queue.
  if (ticket.parkScope !== 'branch' || !(profile.poolScopes ?? []).includes('branch')) return false
  return Boolean(ticket.queueId) && profile.queueIds.includes(ticket.queueId ?? '')
}

/**
 * Whether this desk or the person at it holds a parked customer: held for this
 * desk or this person, or put in the branch pool by this desk. Mirrors the
 * server's queue.HeldFor: calling them back is never refused or out of turn.
 */
/** Sent to this desk or person by somebody else, or back by a return: the
 *  Transferred list. Mirrors the server's queue.SentTo. */
export function sentTo(ticket: Ticket, counterId: string, staffId = ''): boolean {
  if (!heldFor(ticket, counterId, staffId)) return false
  if (ticket.returned) return true
  return ticket.parkScope !== 'branch' && ticket.parkedByCounterId !== counterId
}

export function heldFor(ticket: Ticket, counterId: string, staffId = ''): boolean {
  if (ticket.state !== 'parked' || !ticket.parkedAt) return false
  switch (ticket.parkScope) {
    case 'staff':
      return Boolean(staffId) && ticket.poolStaffId === staffId
    case 'desk':
      return Boolean(counterId) && ticket.poolCounterId === counterId
    case 'branch':
      return Boolean(counterId) && ticket.parkedByCounterId === counterId
    default:
      return false
  }
}

/**
 * The pools a desk on this profile may park a customer into.
 *
 * The other half of callableFromPool, and the half that was missing. A screen that
 * offers "keep them for this desk only" to a profile with no pools has offered a
 * park nothing will ever call back: the customer is set aside for a desk that is
 * not allowed to fetch them, and they sit there until somebody notices. Straight
 * after the upgrade that is every profile in the branch, because the column
 * defaults to empty.
 *
 * Widest first, so the head of the list is also the safe default. The branch pool
 * is the one that cannot strand anybody — a desk pool with nobody at the desk is a
 * customer waiting on a closed window.
 *
 * An empty answer is not a degraded one. It means this desk has no choice to make,
 * and a park should go back to being what it was before pools existed: set aside,
 * fetched by hand.
 */
export function offeredParkScopes(poolScopes: readonly ParkScope[] | undefined): ParkScope[] {
  const drawn = poolScopes ?? []
  return (['branch', 'desk'] as const).filter((scope) => drawn.includes(scope))
}

/**
 * The scope a park should actually be sent with.
 *
 * Undefined where the desk draws from nothing, which is the client saying nothing
 * about pools at all — exactly what every client sent before there was a choice.
 *
 * A chosen scope the profile does not draw from falls back to the widest one it
 * does, rather than being sent and stranding somebody. That is not a hypothetical:
 * the default is the branch pool, and a profile that draws only from the desk pool
 * would otherwise send "branch" on the operator's first park of the day.
 */
export function parkScopeToSend(
  chosen: ParkScope,
  offered: readonly ParkScope[],
): ParkScope | undefined {
  if (offered.length === 0) return undefined
  return offered.includes(chosen) ? chosen : offered[0]
}

/**
 * waitedSeconds freezes at the first call, exactly as the reports measure it: how
 * long the customer waited to be called. Not the order a line is called in — a
 * customer who rejoins a line is ranked by callingWaitSeconds, which goes on
 * counting.
 */
export function waitedSeconds(ticket: Ticket, now: number = Date.now()): number {
  // From the later of the door and the booked time, as the server's WaitedFor:
  // a customer who sat in the hall before their booking was not kept waiting.
  const issued = Date.parse(ticket.issuedAt)
  const booked = ticket.appointmentAt ? Date.parse(ticket.appointmentAt) : issued
  const from = Math.max(issued, booked)
  const until = ticket.firstCalledAt ? Date.parse(ticket.firstCalledAt) : now
  return Math.max(0, Math.round((until - from) / 1000))
}

function byIssuedAt(a: Ticket, b: Ticket): number {
  const d = Date.parse(a.issuedAt) - Date.parse(b.issuedAt)
  // Ties break on the number, so the same input always renders in the same order.
  return d !== 0 ? d : a.displayNumber.localeCompare(b.displayNumber)
}

function bySortOrder(a: CounterView, b: CounterView): number {
  return a.sortOrder - b.sortOrder || a.code.localeCompare(b.code)
}
